import { ArrowLeft, Check, CheckCircle2, History, AlertCircle, PenLine, Sparkles } from "lucide-react";
import type { FormulatorDraftSource } from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdoContent } from "@/components/ado/ado-content";
import { ReadinessBadge } from "@/components/ado/readiness-badge";
import { MissingItemsList } from "@/components/formulator/missing-items-list";
import type { FieldDiff, InterviewReview } from "@/lib/api/work-item-interview";
import { cn } from "@/lib/utils";

const KIND_LABELS: Record<InterviewReview["kind"], string> = {
  bug: "Bug",
  story: "User Story",
};

/** Who wrote the text, in plain words (epic PI-SPEC-SW-E321). */
export const DRAFT_SOURCE_TEXT: Record<FormulatorDraftSource, string> = {
  claude: "Written by Claude from your answers",
  person: "Edited by you",
};

/**
 * The one-line source label of a review: who wrote the text it shows. None
 * when the text was put together from the answers (no stored draft, or an
 * older SpecTree that sends no draft fields). Pure; exported for tests.
 */
export function draftSourceText(review: Pick<InterviewReview, "draftStatus" | "draftSource">): string | null {
  if (review.draftStatus !== "current" || !review.draftSource) return null;
  return DRAFT_SOURCE_TEXT[review.draftSource];
}

/** The source label as a line of its own; renders nothing for text put together from the answers. */
export function DraftSourceLabel({
  review,
  className,
}: {
  review: Pick<InterviewReview, "draftStatus" | "draftSource">;
  className?: string;
}) {
  const text = draftSourceText(review);
  if (text === null) return null;
  const Icon = review.draftSource === "person" ? PenLine : Sparkles;
  return (
    <p
      className={cn("flex items-center gap-1.5 text-[12px] text-muted-foreground", className)}
      data-testid="draft-source"
      data-source={review.draftSource ?? undefined}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {text}
    </p>
  );
}

/** One side of a field: tracker HTML through AdoContent (sanitised, ADO's lists and headings), plain text as text. */
function FieldValue({ value, format, testId }: { value: string; format: FieldDiff["format"]; testId: string }) {
  if (format === "html") {
    return (
      <AdoContent
        html={value}
        tone="dark"
        className="text-sm"
        testId={testId}
        empty={
          <p className="text-sm italic text-muted-foreground" data-testid={testId}>
            Empty
          </p>
        }
      />
    );
  }

  return value.trim() ? (
    <p className="whitespace-pre-wrap break-words text-sm" data-testid={testId}>
      {value}
    </p>
  ) : (
    <p className="text-sm italic text-muted-foreground" data-testid={testId}>
      Empty
    </p>
  );
}

function FieldDiffCard({ diff, isCreate }: { diff: FieldDiff; isCreate: boolean }) {
  return (
    <Card className="glass-card" data-testid="field-diff" data-field={diff.field}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{diff.label}</CardTitle>
      </CardHeader>
      <CardContent>
        {isCreate ? (
          <FieldValue value={diff.after} format={diff.format} testId="field-after" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5 rounded-lg border border-red-500/20 bg-red-500/[0.04] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-red-300/80">
                Before (current in Azure DevOps)
              </p>
              <FieldValue value={diff.before} format={diff.format} testId="field-before" />
            </div>
            <div className="space-y-1.5 rounded-lg border border-green-500/20 bg-green-500/[0.04] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-green-300/80">
                After (will be written)
              </p>
              <FieldValue value={diff.after} format={diff.format} testId="field-after" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The changed fields as before/after cards (after only in create mode), both
 * sanitised. When the text comes from a stored draft (epic PI-SPEC-SW-E321)
 * the after side is that draft as SpecTree renders it. The Toolbox Formulator's review (PI-SPEC-SW-1418) shows this in
 * improve mode beside its own Update in ADO action.
 */
export function FieldDiffList({ review }: { review: InterviewReview }) {
  const isCreate = review.mode === "create";
  return (
    <div className="space-y-4" data-testid="field-diff-list">
      {review.fields.map((diff) => (
        <FieldDiffCard key={diff.field} diff={diff} isCreate={isCreate} />
      ))}
    </div>
  );
}

export interface FieldDiffViewProps {
  review: InterviewReview;
  /**
   * Called only when the user clicks Approve, with the `diffHash` of the
   * review they were shown. The caller submits it; nothing is written before.
   */
  onApprove: (diffHash: string) => void;
  /** Back to the interview without writing anything. */
  onBack: () => void;
  isSubmitting?: boolean;
}

/**
 * The before/after diff the user must approve before the Formulator writes to
 * Azure DevOps (PI-SPEC-SW-1364, D7). Every changed field shows the current
 * tracker value and the value that will replace it, both sanitised. The
 * projected readiness is the rubric's verdict on the item as it would read
 * after the write.
 *
 * Approval here is a real content decision: the user is reviewing an
 * overwrite of live board text, not confirming a formality.
 */
export function FieldDiffView({ review, onApprove, onBack, isSubmitting = false }: FieldDiffViewProps) {
  const isCreate = review.mode === "create";
  const { grooming, sprint, readyForGrooming } = review.projectedReadiness;
  const nothingToWrite = review.fields.length === 0;

  return (
    <div className="space-y-5" data-testid="field-diff-view">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">
          {isCreate ? "Review the new item" : "Review the changes"}
        </h2>
        <p className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="overwrite-notice">
          <History className="mt-0.5 h-4 w-4 shrink-0" />
          {isCreate
            ? `Approving creates a new ${KIND_LABELS[review.kind]} in ${review.container} under your Azure DevOps identity.`
            : `Approving replaces the current text of these fields on item #${review.itemId ?? ""} in Azure DevOps, as you. The original text stays in the item's history.`}
        </p>
        <DraftSourceLabel review={review} />
      </div>

      <Card className="glass-card" data-testid="projected-readiness">
        <CardContent className="space-y-3 pt-6">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">After {isCreate ? "creation" : "these changes"}:</span>
            <ReadinessBadge level="grooming" tier={grooming.tier} />
            <ReadinessBadge level="sprint" tier={sprint.tier} />
          </div>
          {readyForGrooming ? (
            <p className="flex items-center gap-2 text-sm text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              The item will be ready for grooming ({grooming.score}/100).
            </p>
          ) : (
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="h-4 w-4" />
                The item will still not be ready for grooming ({grooming.score}/100).
              </p>
              {grooming.missing.length > 0 && (
                <MissingItemsList items={grooming.missing} testId="projected-missing" />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {nothingToWrite ? (
        <p className="rounded-lg border border-white/[0.08] p-4 text-sm text-muted-foreground" data-testid="no-changes">
          There is nothing to write yet. Go back and answer the remaining questions.
        </p>
      ) : (
        <FieldDiffList review={review} />
      )}

      <div className="flex justify-end gap-3 border-t border-white/[0.08] pt-4">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={() => {
            onApprove(review.diffHash);
          }}
          disabled={isSubmitting || nothingToWrite}
          data-testid="approve-diff"
        >
          <Check className="mr-2 h-4 w-4" />
          {isSubmitting ? "Writing to Azure DevOps..." : isCreate ? "Approve and create" : "Approve and update"}
        </Button>
      </div>
    </div>
  );
}
